import { type FC, useContext, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useBuyScratchTicketMutation,
  useGetScratchEventQuery,
  useLazyGetScratchEventQuery,
} from '@/api/queries/scratch';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { openTgInvoice } from '@/common/lib/openTgInvoice';
import { Playdeck, SendAnalitics } from '@/common/lib/playdeck';
import { type BuyDiceForTonPaymentResponseType } from '@/common/types/payment';
import { type ScratchOfferId } from '@/common/types/scratch';
import { H1, H2, StringSmall } from '@/components/Typography/Typography';
import { Close } from '@/components/ui/Close/Close';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import {
  changePaymentStatus,
  clearPendingPurchase,
  setPendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { applyBoughtTicket } from '@/redux/reducers/scratchReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

interface BuyScratchTicketModalProps {
  offerId: ScratchOfferId;
  rewardKey: string;
}

/**
 * Currency-selection modal for paid scratch offers (basic / standard / premium).
 * Mirrors the BuyBingoBallsModal `ConfirmBuy` step — `TONandStarsBuyButtons`
 * with the USDT switch, plus the standard pending/success/close states.
 */
const BuyScratchTicketModal: FC<BuyScratchTicketModalProps> = ({
  offerId,
  rewardKey,
}) => {
  const { t } = useTranslation();
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const paymentStatus = usePaymentStatus();
  const { data: event } = useGetScratchEventQuery();
  const [refetchEvent] = useLazyGetScratchEventQuery();
  const [purchase, { isLoading }] = useBuyScratchTicketMutation();
  const [getMyInfo] = useGetMyInfoMutation();
  const connectWalletAndDoTransaction = useTonAutoConnect();

  const offer = event?.offers.find((o) => o.id === offerId) ?? null;

  useLayoutEffect(() => {
    dispatch(changePaymentStatus(undefined));
  }, [dispatch]);

  const buildAnalitics = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): {
    item: 'scratch_ticket';
    item_id: string;
    price: number;
    currency: 'ton' | 'xtr' | 'usdt';
  } | null => {
    if (!offer || offer.amountUsd == null) return null;

    return {
      item: 'scratch_ticket',
      item_id: `${offerId}-${rewardKey}`,
      price: offer.amountUsd,
      currency,
    };
  };

  const buildExtras = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): Record<string, unknown> | null => {
    if (!offer) return null;

    return {
      offerId,
      reward_key: rewardKey,
      price: offer.amountUsd ?? 0,
      currency,
    };
  };

  const markPaymentPending = (currency: 'ton' | 'xtr'): void => {
    const payload = buildAnalitics(currency);
    const extras = buildExtras(currency);

    if (!payload || !extras) return;
    dispatch(setPendingPurchase({ ...payload, extras }));
    SendAnalitics.purchasePending(payload);
  };

  const buyTon = async (): Promise<void> => {
    if (!offer) return;
    connectWalletAndDoTransaction(
      async () =>
        (await purchase({
          offerId,
          rewardKey,
          currency: 'ton',
        }).unwrap()) as unknown as BuyDiceForTonPaymentResponseType,
      {
        onSuccess: () => {
          ToastNotification(t('Payment will be credited soon.'));
          markPaymentPending('ton');
        },
        onError: () => {
          ToastNotification(t('Something went wrong, please try again'));
        },
      },
    );
  };

  const buyStars = async (): Promise<void> => {
    if (!offer) return;
    const response = await purchase({
      offerId,
      rewardKey,
      currency: 'xtr',
    }).unwrap();

    if (response.link) {
      openTgInvoice(response.link, {
        onSuccess: () => {
          markPaymentPending('xtr');
        },
        playdeckRequestPayload: {
          amount: offer.amountStars ?? 0,
          description: 'buy scratch ticket',
          externalId: `buy-scratch-${offerId}-${Date.now()}`,
        },
      });
    } else if (response.ticket) {
      // Synchronous (rare for paid flow but possible) — drop straight in.
      dispatch(applyBoughtTicket(response.ticket));
      closeModal();
    }
  };

  const buyUsdt = async (): Promise<void> => {
    if (!offer) return;

    try {
      const response = await purchase({
        offerId,
        rewardKey,
        currency: 'usdt',
      }).unwrap();

      if (response.ticket) {
        dispatch(applyBoughtTicket(response.ticket));
      }

      // Close the USDT confirm dialog (stacked as second/third modal) —
      // same pattern as BuyBingoBallsModal.buyUsdt.
      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }

      await getMyInfo();
      // USDT response is empty per BE — the canonical ticket is delivered
      // via SCRATCH_TICKET_BOUGHT WS, but we also refetch /scratch/event
      // immediately so the offer card flips to "scratching" without waiting.
      await refetchEvent();
      dispatch(changePaymentStatus('success'));

      const payload = buildAnalitics('usdt');
      const extras = buildExtras('usdt');

      if (payload && extras) {
        SendAnalitics.purchaseSuccessfull(payload);
        Playdeck.sendAnalitics({
          type: 'custom_event',
          name: 'scratch_purchase_completed',
          user_properties: {},
          event_properties: extras,
        });
      }

      ToastNotification(t('Success. Ticket credited.'));
    } catch (err) {
      const msg =
        (err as { data?: { message?: string } })?.data?.message ?? 'Error';
      showToast(msg);
      ToastNotification(t('Something went wrong, please try again'));
    }
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    if (offer && offer.amountUsd != null) {
      SendAnalitics.purchaseAttempt({
        item: 'scratch_ticket',
        item_id: `${offerId}-${rewardKey}`,
        price: offer.amountUsd,
        currency,
      });
    }

    const action = currency === 'TON' ? buyTon() : buyStars();
    action
      .then(() => {
        dispatch(changePaymentStatus('pending'));
      })
      .catch((err) => {
        const msg =
          (err as { data?: { message?: string } })?.data?.message ?? '';

        if (msg) showToast(msg);
        dispatch(changePaymentStatus(undefined));
        dispatch(clearPendingPurchase());
      });
  };

  if (!offer) return null;

  return (
    <div className="relative w-full px-4 pt-5 pb-6 flex flex-col items-center">
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : paymentStatus === 'success' ? (
        <SuccessfullStatus>
          <div className="bg-white/10 rounded-[8px] relative p-[13px] opacity-[0.6] mt-8 flex items-center justify-center">
            <img
              src="/img/tap-power.png"
              alt=""
              className="w-24 h-24 object-contain"
            />
          </div>
        </SuccessfullStatus>
      ) : (
        <>
          <div className="w-full flex flex-col items-center relative z-[1]">
            <H1 weight="extrabold" className="mb-6">
              {t('Purchase')}
            </H1>
            <H2 weight="bold" className="mb-2">
              {t('Buy a scratch ticket')}
            </H2>
            <StringSmall className="!text-white/80 text-center mb-5 max-w-[17.125rem]">
              {t('Match all 3 cells with your chosen reward to win.')}
            </StringSmall>
          </div>

          {/* Generic "scratch" mark — same finger-on-foil art as the
           * Game Center Scratch card. Doesn't preview the chosen reward;
           * the reward is already shown in the offer card on the page. */}
          <div className="relative flex flex-col items-center mb-2 mt-2">
            <img
              src="/img/tap-power.png"
              alt=""
              className="w-[120px] h-[120px] object-contain"
              draggable={false}
            />
          </div>

          <div className="w-full mb-[1.875rem] mt-6">
            <TONandStarsBuyButtons
              onClick={onClick}
              isLoading={isLoading}
              price={multiplication(
                offer.amountUsd ?? 0,
                additionalDiscountForUsdt.coefficient,
              )}
              discount={additionalDiscountForUsdt.percentage}
              starPrice={offer.amountStars ?? 0}
              mustBeUsdtButton={true}
              onUsdtBuy={buyUsdt}
              variant="switch"
              classNames="!px-0"
            />
          </div>
        </>
      )}

      <Close
        onClick={() => closeModal()}
        imageVariant="modal"
        size="lg"
        className="w-16 h-16 shrink-0 mt-4"
      />
    </div>
  );
};

export { BuyScratchTicketModal };
